import { SerializedComponent, getAppTabs } from "../../components/SerializedApp";
import {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { PropertySection } from "../components/property-sections";
import { getScreenProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    PropertyKind,
    TabDescription,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler } from "./base";

interface NavigateToTabActionDescription extends ActionDescription {
    readonly screen: PropertyDescription | undefined;
}

function getTab(
    ccc: AppDescriptionContext,
    desc: NavigateToTabActionDescription
): { tabIndex: number; tab: TabDescription } | undefined {
    const { appDescription } = ccc;
    if (appDescription === undefined) return undefined;

    const screenName = getScreenProperty(desc.screen);
    if (screenName === undefined) return undefined;
    const tabs = getAppTabs(appDescription);
    const tabIndex = tabs.findIndex(t => getScreenProperty(t.screenName) === screenName);
    const tab = tabs[tabIndex];
    if (tab === undefined || tab.hidden) return undefined;
    return { tab, tabIndex };
}

export class NavigateToTabHandler extends BaseActionHandler<NavigateToTabActionDescription> {
    public readonly kind = ActionKind.NavigateToTab;
    // FIXME: proper icon
    public readonly iconName = "clipboard";

    public getDescriptor(): ActionDescriptor {
        return {
            name: "Go to Tab",
            needsScreenContext: true,
            properties: [
                {
                    property: { name: "screen" },
                    section: PropertySection.Data,
                    label: "Tab",
                    kind: PropertyKind.Screen,
                },
            ],
        };
    }

    public getDescriptionToWalk(
        desc: NavigateToTabActionDescription,
        ccc: ComponentConfiguratorContext
    ): NavigateToTabActionDescription {
        const maybeTab = getTab(ccc, desc);
        if (maybeTab === undefined) {
            return { ...desc, screen: undefined };
        } else {
            return desc;
        }
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: NavigateToTabActionDescription
    ): SerializedComponent | undefined {
        const { b } = builder;

        const maybeTab = getTab(builder, desc);
        if (maybeTab === undefined) return undefined;

        const { tabIndex } = maybeTab;

        return b.action("SetCurrentTab", { index: b.constant(tabIndex) });
    }

    public getTokenizedDescription(
        desc: NavigateToTabActionDescription,
        _tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const maybeTab = getTab(ccc, desc);
        if (maybeTab === undefined) return undefined;

        return [
            {
                kind: "column",
                value: maybeTab.tab.title ?? maybeTab.tab.icon,
            },
        ];
    }

    public inflate(ib: WireInflationBackend, desc: NavigateToTabActionDescription): WireActionHydrator | undefined {
        const maybeTab = getTab(ib, desc);
        const screenName = getScreenProperty(maybeTab?.tab.screenName);
        if (screenName === undefined) return undefined;

        return hb => {
            if (!hb.isTabVisible(screenName)) return undefined;
            return async ab => {
                ab.navigateToTab(screenName);
                return true;
            };
        };
    }
}
